/**
 * React Query hooks - the seam pages use instead of importing mockData.
 * Each returns { data, isLoading, isError, ... }.
 */
import { useQuery } from '@tanstack/react-query';
import * as catalog from './catalog';
import * as market from './market';
import * as dashboard from './dashboard';
import * as admin from './admin';
import * as account from './account';

// ---- catalog ----
export const useStates = () => useQuery({ queryKey: ['states'], queryFn: catalog.getStates });
export const useMarkets = (params) =>
  useQuery({ queryKey: ['markets', params], queryFn: () => catalog.getMarkets(params) });
export const useCategories = () => useQuery({ queryKey: ['categories'], queryFn: catalog.getCategories });
export const useCommodities = (params) =>
  useQuery({ queryKey: ['commodities', params], queryFn: () => catalog.getCommodities(params) });
export const useCommodityCoverage = () =>
  useQuery({ queryKey: ['commodity-coverage'], queryFn: catalog.getCommodityCoverage });
export const useAliases = (commodityId) =>
  useQuery({ queryKey: ['aliases', commodityId], queryFn: () => catalog.getAliases(commodityId) });
export const useGlobalCommodities = () =>
  useQuery({ queryKey: ['global-commodities'], queryFn: catalog.getGlobalCommodities });
export const useGlobalSpecies = () =>
  useQuery({ queryKey: ['global-species'], queryFn: catalog.getGlobalSpecies });

// ---- market / series ----
// Carries checkedAt as well as the observation date, so the interface can
// say when we last asked CBN rather than only what CBN last published.
export const useFxLatest = (pair = 'NGN/USD') =>
  useQuery({ queryKey: ['fx-latest', pair], queryFn: () => market.getFxLatest(pair) });
export const useFxSeries = (range = 'ALL', pair = 'NGN/USD', opts = {}) =>
  useQuery({
    queryKey: ['fx', range, pair],
    queryFn: () => market.getFxSeries(range, pair),
    ...opts,
  });
export const useGlobalPrices = (range = 'ALL') =>
  useQuery({ queryKey: ['global-prices', range], queryFn: () => market.getGlobalPrices(range) });
export const useSpeciesPrices = (range = 'ALL') =>
  useQuery({ queryKey: ['species-prices', range], queryFn: () => market.getSpeciesPrices(range) });
export const useWorldReference = (commodityId, months, options = {}) =>
  useQuery({
    queryKey: ['world-reference', commodityId, months],
    queryFn: () => market.getWorldReference(commodityId, months),
    ...options,
  });
// Several commodities in one request. The key sorts the ids so that the same
// set in a different order is the same cache entry.
// The catalogue changes about once a year, so it is cached for the session
// rather than refetched per page.
export const useWorldSeriesCatalogue = (options = {}) =>
  useQuery({
    queryKey: ['world-series-catalogue'],
    queryFn: () => market.getWorldSeriesCatalogue(),
    staleTime: 60 * 60 * 1000,
    ...options,
  });
export const useWorldReferenceBatch = (commodityIds, months, options = {}) =>
  useQuery({
    queryKey: ['world-reference-batch', [...(commodityIds || [])].sort().join(','), months],
    queryFn: () => market.getWorldReferenceBatch(commodityIds, months),
    enabled: (commodityIds || []).length > 0,
    ...options,
  });
export const useLocalCommoditySeries = (range = 'ALL') =>
  useQuery({ queryKey: ['local-series', range], queryFn: () => market.getLocalCommoditySeries(range) });
export const useLocalPrices = (params, opts = {}) =>
  useQuery({ queryKey: ['local-prices', params], queryFn: () => market.getLocalPrices(params), ...opts });
export const usePriceMovements = (state) =>
  useQuery({ queryKey: ['price-movements', state], queryFn: () => market.getPriceMovements(state) });

// ---- dashboard ----
export const useDashboardMetrics = () =>
  useQuery({ queryKey: ['dashboard-metrics'], queryFn: dashboard.getDashboardMetrics });

// ---- admin ----
export const useUploadHistory = () =>
  useQuery({ queryKey: ['upload-history'], queryFn: admin.getUploadHistory });
// `enabled` is passed through so the layout banner can skip the call entirely
// for a customer, who would only get a 403 from an admin-guarded endpoint.
export const useUploadFreshness = (options = {}) =>
  useQuery({ queryKey: ['upload-freshness'], queryFn: admin.getUploadFreshness, ...options });
export const useUploadHistoryPaged = (limit, offset) =>
  useQuery({
    queryKey: ['upload-history-paged', limit, offset],
    queryFn: () => admin.getUploadHistoryPaged(limit, offset),
    placeholderData: (prev) => prev,
  });
export const useUploadDetail = (uploadId) =>
  useQuery({
    queryKey: ['upload-detail', uploadId],
    queryFn: () => admin.getUploadDetail(uploadId),
    enabled: !!uploadId,
  });
export const useUsers = () => useQuery({ queryKey: ['users'], queryFn: admin.getUsers });

// ---- account (premium) ----
export const usePortfolio = () => useQuery({ queryKey: ['portfolio'], queryFn: account.getPortfolio });
export const useAlerts = (opts = {}) => useQuery({ queryKey: ['alerts'], queryFn: account.getAlerts, ...opts });
